import { createContext, useCallback, useContext, useRef, useState, type ReactNode } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

/**
 * App-wide replacements for the browser's `window.confirm` / `window.prompt`,
 * rendered as themed shadcn dialogs. Promise-based so call sites read almost
 * identically to the native ones:
 *
 *   const confirm = useConfirm();
 *   if (!(await confirm("Delete this?"))) return;
 *
 *   const prompt = usePrompt();
 *   const name = await prompt({ title: "New collection", label: "Name" });
 *   if (!name) return;
 */

export interface ConfirmOptions {
  title?: string;
  description?: ReactNode;
  confirmText?: string;
  cancelText?: string;
  /** Style the confirm button as destructive (red). */
  destructive?: boolean;
}

export interface PromptOptions {
  title?: string;
  description?: ReactNode;
  label?: string;
  placeholder?: string;
  defaultValue?: string;
  confirmText?: string;
  cancelText?: string;
  /** Reject empty input (default true). */
  required?: boolean;
}

type ConfirmFn = (opts: ConfirmOptions | string) => Promise<boolean>;
type PromptFn = (opts: PromptOptions | string) => Promise<string | null>;

interface DialogsApi {
  confirm: ConfirmFn;
  prompt: PromptFn;
}

const DialogsContext = createContext<DialogsApi | null>(null);

interface ConfirmState extends ConfirmOptions {
  resolve: (v: boolean) => void;
}
interface PromptState extends PromptOptions {
  resolve: (v: string | null) => void;
}

export const DialogProvider = ({ children }: { children: ReactNode }) => {
  const [confirmState, setConfirmState] = useState<ConfirmState | null>(null);
  const [promptState, setPromptState] = useState<PromptState | null>(null);
  const [promptValue, setPromptValue] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  const confirm = useCallback<ConfirmFn>(
    (opts) =>
      new Promise<boolean>((resolve) => {
        const o = typeof opts === "string" ? { description: opts } : opts;
        setConfirmState({ ...o, resolve });
      }),
    [],
  );

  const prompt = useCallback<PromptFn>(
    (opts) =>
      new Promise<string | null>((resolve) => {
        const o = typeof opts === "string" ? { title: opts } : opts;
        setPromptValue(o.defaultValue ?? "");
        setPromptState({ ...o, resolve });
      }),
    [],
  );

  const closeConfirm = (result: boolean) => {
    confirmState?.resolve(result);
    setConfirmState(null);
  };
  const closePrompt = (result: string | null) => {
    promptState?.resolve(result);
    setPromptState(null);
  };

  const promptRequired = promptState?.required !== false;
  const promptValid = !promptRequired || promptValue.trim().length > 0;

  return (
    <DialogsContext.Provider value={{ confirm, prompt }}>
      {children}

      <AlertDialog open={!!confirmState} onOpenChange={(o) => !o && closeConfirm(false)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{confirmState?.title ?? "Are you sure?"}</AlertDialogTitle>
            {confirmState?.description != null && (
              <AlertDialogDescription>{confirmState.description}</AlertDialogDescription>
            )}
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => closeConfirm(false)}>
              {confirmState?.cancelText ?? "Cancel"}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => closeConfirm(true)}
              className={
                confirmState?.destructive
                  ? "bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  : undefined
              }
            >
              {confirmState?.confirmText ?? "Confirm"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={!!promptState} onOpenChange={(o) => !o && closePrompt(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{promptState?.title ?? "Enter a value"}</DialogTitle>
            {promptState?.description != null && (
              <DialogDescription>{promptState.description}</DialogDescription>
            )}
          </DialogHeader>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (promptValid) closePrompt(promptValue.trim());
            }}
            className="space-y-2"
          >
            {promptState?.label && <Label className="text-sm">{promptState.label}</Label>}
            <Input
              ref={inputRef}
              autoFocus
              value={promptValue}
              placeholder={promptState?.placeholder}
              onChange={(e) => setPromptValue(e.target.value)}
            />
            <DialogFooter className="mt-4">
              <Button type="button" variant="ghost" onClick={() => closePrompt(null)}>
                {promptState?.cancelText ?? "Cancel"}
              </Button>
              <Button type="submit" disabled={!promptValid}>
                {promptState?.confirmText ?? "OK"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </DialogsContext.Provider>
  );
};

// Safe fallback when a component is rendered outside <DialogProvider> (e.g. in
// isolated unit tests). The real app always mounts the provider in App.tsx, so
// this only avoids crashes in those edge renders — confirm resolves false and
// prompt resolves null (no destructive action proceeds).
const FALLBACK: DialogsApi = {
  confirm: () => Promise.resolve(false),
  prompt: () => Promise.resolve(null),
};

const useDialogs = (): DialogsApi => useContext(DialogsContext) ?? FALLBACK;

export const useConfirm = (): ConfirmFn => useDialogs().confirm;
export const usePrompt = (): PromptFn => useDialogs().prompt;
